import React, {useRef, useEffect} from "react";
import {Input} from "reactstrap";
import {observer} from "mobx-react-lite";
import {NumberObservable} from "../../../stores/observables/numberObservable";
import "./input.sass";
import classNames from "classnames";

export interface INumberBoxProps {
  /**
   * Value to be rendered.
   */
  value: NumberObservable;

  /**
   * The placeholder text
   */
  placeholder?: string;

  /**
   * The validation error to show
   */
  errorText?: string | null;

  /**
   * Optional. Adds a prefix to the number.
   */
  prefix?: string;

  /**
   * Optional. Adds a postfix to the number.
   */
  postfix?: string;

  /**
   * A flag that indicates whether the user is allowed to manually enter values
   */
  readonly?: boolean;

  /**
   * Optional. The increment/decrement of the number. For instance, when set to 2, the increment for 1 will be 1 + 2. Default is 1.
   */
  step?: number;

  /**
   * Override for disabling the input. If this is null or false, we use the value's disabled property.
   */
  disabled?: boolean;

  /**
   * Optional. When set to true, it does not allow the user to insert a number directly into the NumberBox, only the NumberBox's spinner, up, and down arrow key could change the number.
   * Default is false.
   */
  disableKeyInput?: boolean;
}

const inputPaddingOffset = 26;

const NumberBox: React.FC<INumberBoxProps> = ({
  value,
  placeholder,
  errorText = null,
  prefix,
  postfix,
  readonly = false,
  disabled,
  step,
  disableKeyInput
}) => {
  function handleTextChange(event: React.ChangeEvent<HTMLInputElement>) {
    const inputValue = event.target.value;
    if (inputValue === "") {
      value.setNumber(undefined);
      return;
    }
    let num = parseFloat(inputValue);
    if (isNaN(num)) {
      value.setNumber(0);
      return;
    }
    let cleanNum = num.toFixed(value.decimalPlaces);
    value.setNumber(parseFloat(cleanNum));
  }

  const postfixRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let inputWidth: number = 0;
    if (postfixRef.current) {
      inputWidth = inputPaddingOffset + postfixRef.current.offsetWidth;
      if (inputRef && inputRef.current) {
        inputRef.current!.style.setProperty(
          "padding-right",
          `${inputWidth}px`,
          "important"
        );
      }
    }
  }, [postfix, value.isValid]);

  return (
    <div
      className={classNames("input-box", {
        "has-prefix": prefix,
        "is-invalid": !value.isValid
      })}
    >
      {prefix && (
        <div
          className={classNames("prefix", {
            disabled: value.isDisabled
          })}
        >
          {prefix}
        </div>
      )}
      <Input
        step={step ?? 1}
        data-testid="numberBox"
        className={classNames({
          disabled: value.isDisabled
        })}
        type="number"
        value={value.number ?? ""}
        onChange={handleTextChange}
        onFocus={(e) => e.target.select()}
        onKeyDown={(e) => {
          if (
            ["e", "E", "+", "-"].includes(e.key) ||
            (disableKeyInput &&
              !["ARROWUP", "ARROWDOWN", "SHIFT", "TAB"].includes(
                e.key.toUpperCase()
              ))
          ) {
            e.preventDefault();
          }
        }}
        readOnly={readonly}
        placeholder={placeholder}
        invalid={!value.isValid}
        min={value.min}
        max={value.max}
        disabled={value.isDisabled || disabled}
        innerRef={inputRef}
      />
      {postfix && (
        <div
          className={classNames("postfix", {
            disabled: value.isDisabled
          })}
          ref={postfixRef}
        >
          {postfix}
        </div>
      )}
      {errorText && <label>{errorText}</label>}
    </div>
  );
};

export default observer(NumberBox);
